import { useMemo, type CSSProperties } from "react";
import { useNoteLabel } from "@/hooks/use-notation";

type Props = {
  /** MIDIs to highlight on the keyboard. */
  midis: number[];
  /** Lowest MIDI shown, must align with a C (mod 12 === 0). Default C3 (48). */
  lowMidi?: number;
  /** Number of octaves displayed. Default 2. */
  octaves?: number;
  /** Highlight color for non-bass highlighted keys. */
  highlightColor?: string;
  /** Optional accent color for the lowest highlighted note (the bass). */
  bassColor?: string;
  /**
   * Per-note colors for keys that are **already highlighted**, overriding
   * `highlightColor` and `bassColor`. Lets a caller paint by hand instead of
   * washing every highlighted key in one colour.
   *
   * It refines a highlight, it never creates one: `midis` alone decides
   * which keys are lit, so a caller can pass colours for notes it has
   * deliberately excluded without them reappearing.
   */
  midiColors?: ReadonlyMap<number, string>;
  /** Show note labels on highlighted keys. */
  showLabels?: boolean;
  /**
   * Discreet labels drawn on keys **whether or not they are highlighted**,
   * for callers that need a reference to count from rather than an answer.
   *
   * Distinct from `showLabels`, which names what is already lit and so tells
   * the player nothing they cannot see. A landmark is the opposite: it names
   * a key that is *not* the target, so a hand hunting for one can count from
   * it the way a pianist counts from a C.
   *
   * Keys outside the window are ignored, and a landmark on a highlighted key
   * is drawn on top of its fill rather than replacing the highlight.
   */
  landmarks?: ReadonlyMap<number, string>;
  /** Spelling helper, "sharp" or "flat", used only when labels are shown. */
  preferFlats?: boolean;
  className?: string;
  /**
   * Inline styles on the `<svg>`. Callers that size the keyboard themselves
   * pass an explicit width/height here: `preserveAspectRatio` letterboxes
   * whatever box it is given, so a caller that needs the drawing flush has
   * to supply a box matching the intrinsic ratio.
   */
  style?: CSSProperties;
};

const WHITE_PCS = [0, 2, 4, 5, 7, 9, 11];
const BLACK_PC_OFFSETS: Record<number, number> = {
  1: 0.7,
  3: 1.7,
  6: 3.7,
  8: 4.7,
  10: 5.7,
};

const WHITE_W = 14;
const WHITE_H = 56;
const BLACK_W_RATIO = 0.6;
const BLACK_H_RATIO = 0.62;

const SHARP_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

// ── Shared geometry ───────────────────────────────────────────────
//
// Exported so an overlay can sit on this keyboard and land on the right
// keys. A caller computing its own midi→x mapping would have to be linear
// in pitch, which this layout is not — white keys are evenly spaced and
// black ones are offset between them — so the two would drift apart.

/** Width of the whole keyboard, in viewBox units, for `octaves` octaves. */
export function keyboardViewBoxWidth(octaves: number): number {
  return octaves * 7 * WHITE_W;
}

/** Height of the keyboard, in viewBox units. */
export const KEYBOARD_VIEWBOX_HEIGHT = WHITE_H;

/**
 * Horizontal centre of a key, in viewBox units, or `null` when the midi
 * falls outside the window.
 */
export function keyCenterX(
  midi: number,
  lowMidi: number,
  octaves: number,
): number | null {
  const offset = midi - lowMidi;

  if (offset < 0 || offset >= octaves * 12) {
    return null;
  }

  const oct = Math.floor(offset / 12);
  const pc = offset % 12;
  const whiteIndex = WHITE_PCS.indexOf(pc);

  if (whiteIndex >= 0) {
    return (oct * 7 + whiteIndex) * WHITE_W + WHITE_W / 2;
  }

  const blackOffset = BLACK_PC_OFFSETS[pc];

  return (oct * 7 + blackOffset) * WHITE_W + (WHITE_W * BLACK_W_RATIO) / 2;
}

/**
 * Width of a key and how far down it reaches, in viewBox units.
 *
 * Exported for the same reason as `keyCenterX`: an overlay that wants to
 * mark the foot of a key has to know where that foot is, and a black key
 * stops well above a white one. A caller guessing would put the mark of a
 * black key on the white key behind it.
 */
export function keyFootprint(midi: number): {
  readonly width: number;
  readonly bottom: number;
} {
  const pc = ((midi % 12) + 12) % 12;

  if (BLACK_PC_OFFSETS[pc] === undefined) {
    return { width: WHITE_W, bottom: WHITE_H };
  }

  return { width: WHITE_W * BLACK_W_RATIO, bottom: WHITE_H * BLACK_H_RATIO };
}

type KeyInfo = {
  midi: number;
  pc: number;
  x: number;
  label: string;
};

export function MiniPiano({
  midis,
  lowMidi = 48,
  octaves = 2,
  highlightColor = "var(--color-accent-bright)",
  bassColor,
  midiColors,
  showLabels = false,
  landmarks,
  preferFlats = false,
  className = "",
  style,
}: Props) {
  const blackW = WHITE_W * BLACK_W_RATIO;
  const blackH = WHITE_H * BLACK_H_RATIO;
  const totalWhites = octaves * 7;
  const widthPx = totalWhites * WHITE_W;
  const names = preferFlats ? FLAT_NAMES : SHARP_NAMES;

  const toNoteLabel = useNoteLabel();
  const highlightSet = useMemo(() => new Set(midis), [midis]);
  const bassMidi = midis.length > 0 ? Math.min(...midis) : null;

  const { whites, blacks } = useMemo(() => {
    const w: KeyInfo[] = [];
    const b: KeyInfo[] = [];
    for (let oct = 0; oct < octaves; oct++) {
      for (let i = 0; i < 7; i++) {
        const pc = WHITE_PCS[i];
        const midi = lowMidi + oct * 12 + pc;
        w.push({
          midi,
          pc,
          x: (oct * 7 + i) * WHITE_W,
          label: toNoteLabel(names[pc]),
        });
      }

      for (const [pcStr, offset] of Object.entries(BLACK_PC_OFFSETS)) {
        const pc = Number(pcStr);
        const midi = lowMidi + oct * 12 + pc;
        b.push({
          midi,
          pc,
          x: (oct * 7 + offset) * WHITE_W,
          label: toNoteLabel(names[pc]),
        });
      }
    }

    return { whites: w, blacks: b };
  }, [lowMidi, octaves, names, toNoteLabel]);

  return (
    <svg
      viewBox={`0 0 ${widthPx} ${WHITE_H}`}
      className={className}
      style={style}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {whites.map((k) => {
        const hl = highlightSet.has(k.midi);
        const isBass = bassColor !== undefined && bassMidi === k.midi;
        const fill = hl
          ? midiColors?.get(k.midi) ?? (isBass ? bassColor : highlightColor)
          : "rgba(255,255,255,0.92)";

        return (
          <g key={`w-${k.midi}`}>
            <rect
              x={k.x + 0.4}
              y={0}
              width={WHITE_W - 0.8}
              height={WHITE_H}
              rx={1.2}
              fill={fill}
              stroke="rgba(0,0,0,0.35)"
              strokeWidth={0.4}
            />
            {showLabels && hl && (
              <text
                x={k.x + WHITE_W / 2}
                y={WHITE_H - 6}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={k.label.length > 2 ? 5 : 6}
                fontWeight={700}
                style={{
                  fill: "rgba(0,0,0,0.7)",
                  letterSpacing: "-0.02em",
                  pointerEvents: "none",
                }}
              >
                {k.label}
              </text>
            )}
            {landmarks?.has(k.midi) && !(showLabels && hl) && (
              <text
                x={k.x + WHITE_W / 2}
                y={WHITE_H - 5}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={5.5}
                fontWeight={700}
                style={{
                  // 0.42 put the landmark at about 3.5:1 on a white key, under
                  // the 4.5:1 body text needs, and it is the reference a player
                  // counts from — the one label on this keyboard that has to be
                  // read rather than glanced at.
                  fill: "rgba(0,0,0,0.66)",
                  letterSpacing: "-0.02em",
                  pointerEvents: "none",
                }}
              >
                {landmarks.get(k.midi)}
              </text>
            )}
          </g>
        );
      })}

      {blacks.map((k) => {
        const hl = highlightSet.has(k.midi);
        const isBass = bassColor !== undefined && bassMidi === k.midi;
        const fill = hl
          ? midiColors?.get(k.midi) ?? (isBass ? bassColor : highlightColor)
          : "rgba(20,20,22,0.96)";

        return (
          <g key={`b-${k.midi}`}>
            <rect
              x={k.x}
              y={0}
              width={blackW}
              height={blackH}
              rx={1}
              fill={fill}
              stroke="rgba(0,0,0,0.5)"
              strokeWidth={0.4}
            />
            {showLabels && hl && (
              <text
                x={k.x + blackW / 2}
                y={blackH - 4}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={k.label.length > 2 ? 4 : 5}
                fontWeight={700}
                style={{
                  fill: "rgba(255,255,255,0.92)",
                  letterSpacing: "-0.02em",
                  pointerEvents: "none",
                }}
              >
                {k.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
